import { useMemo } from 'react';
import {
  PAYMENT_CREDIT_CARD_LIST_URL,
  PAYMENT_DELETE_COMPLEMENT_CREDIT_CARD_URL,
  PAYMENT_DELETE_CREDIT_CARD_URL,
  PAYMENT_NEW_CREDIT_CARD_URL,
} from '../../constants/constants';
import { handleRequestError, useRestClient } from './useRestClient';
import { CreditCardDTO } from '../DTOs/schedule/CreditCardDTO';
import { useTranslation } from 'react-i18next';

export function useCreditCardService() {
  const { restClient } = useRestClient();
  const { t } = useTranslation('creditCard');

  const createCreditCard = async (
    creditCard: CreditCardDTO,
    locationId: string,
    appointmentTypeId: string
  ) => {
    try {
      const { data } = await restClient.post(PAYMENT_NEW_CREDIT_CARD_URL, {
        ...creditCard,
        locationId,
        appointmentTypeId,
      });
      return data.creditCardId;
    } catch (error) {
      console.log('Error creating credit card service:', error);
      throw handleRequestError(error);
    }
  };

  const deleteCreditCard = async (creditCardId: string) => {
    try {
      const url = `${PAYMENT_DELETE_CREDIT_CARD_URL}/${PAYMENT_DELETE_COMPLEMENT_CREDIT_CARD_URL}/${creditCardId}`;
      const { data } = await restClient.delete(url);
      return data.message;
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  const getCreditCardsList = async (
    locationId: string,
    appointmentTypeId: string
  ): Promise<CreditCardDTO[]> => {
    try {
      const response = await restClient.get(PAYMENT_CREDIT_CARD_LIST_URL, {
        params: { locationId, appointmentTypeId },
      });
      return [...response.data] as CreditCardDTO[];
    } catch (error) {
      throw handleRequestError(error);
    }
  };

  const formatGetCreditCardList = async (
    creditCardId: string | null | undefined,
    locationId: string,
    appointmentTypeId: string
  ): Promise<CreditCardDTO[]> => {
    try {
      const response = await getCreditCardsList(locationId, appointmentTypeId);
      const creditCardList = response as CreditCardDTO[];

      const localPayment: CreditCardDTO = {
        banner: '',
        creditCardId: '',
        lastDigits: '',
      };

      if (creditCardList.length === 0) {
        return [
          localPayment,
          {
            banner: t('screen_credit_card_register_new'),
            creditCardId: 'none',
            lastDigits: '',
          },
        ];
      }

      const selectedCard = creditCardList.find(
        (card) => card.creditCardId === creditCardId
      );

      return selectedCard
        ? [localPayment, selectedCard]
        : [localPayment, creditCardList[0]];
    } catch (error) {
      throw handleRequestError(error);
    }
  };



  return useMemo(
    () => ({
      createCreditCard,
      deleteCreditCard,
      getCreditCardsList,
      formatGetCreditCardList,
    }),
    []
  );
}
